// src/pages/GettingStartedPage.js

import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import "./GettingStartedPage.css";
import { signInWithGoogle, handleLogout } from "../auth";
import { listProjectsOwnedByUser } from "../api/ProjectOntologyManager.js";
import ChatPanel from "../components/ChatPanel";
import ProjectSectionHeader from "../components/ProjectSectionHeader";

// Cookie helpers
function setCookie(
  name,
  value,
  { maxAgeSec = 60 * 60 * 24 * 365, path = "/", sameSite = "Lax" } = {},
) {
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=${path}; max-age=${maxAgeSec}; SameSite=${sameSite}`;
}
function getCookie(name) {
  const needle = `${encodeURIComponent(name)}=`;
  return document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(needle))
    ?.slice(needle.length);
}

export default function GettingStartedPage() {
  const { user, userId, awsCreds, loading } = useOutletContext();

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);

  const [selectedProject, setSelectedProject] = useState(null);
  const [projectsCollapsed, setProjectsCollapsed] = useState(false); // toggle controls visibility
  const [roomDetails] = useState(null); // ChatPanel can handle null

  const navigate = useNavigate();

  // Load projects
  useEffect(() => {
    if (!user || !awsCreds) return;
    let cancelled = false;

    (async () => {
      setProjectsLoading(true);
      setProjectsError(null);
      try {
        const items = await listProjectsOwnedByUser({ userId, awsCreds });
        if (!cancelled) setProjects(items || []);
      } catch (err) {
        console.error("[GettingStartedPage] Failed to list projects:", err);
        if (!cancelled)
          setProjectsError("Unable to load your projects right now.");
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, awsCreds, userId]);

  // Restore project from cookie and default to collapsed (focus on chat) if found
  useEffect(() => {
    if (!projects || projects.length === 0) return;
    const savedId = getCookie("selected_project_id");
    if (!savedId) return;

    const found = projects.find(
      (p) => String(p.id) === decodeURIComponent(savedId),
    );
    if (found) {
      setSelectedProject(found);
      setProjectsCollapsed(true); // cookie present → start collapsed
    }
  }, [projects]);

  const handleCreateProject = () => navigate("/new-project");

  // On click: set cookie, select project, and collapse list
  const handleDesign = (project) => {
    try {
      setCookie("selected_project_id", project.id);
      setSelectedProject(project);
      setProjectsCollapsed(true);
    } catch (err) {
      console.error("[GettingStartedPage] Failed to set project cookie:", err);
    }
  };

  if (loading) return <div>Loading…</div>;

  return (
    <div className="getting-started-page min-h-screen bg-white px-6 py-12">
      <h1 className="text-3xl font-bold mb-6 text-center">
        {user
          ? `Welcome, ${user.displayName}`
          : "Login with Google to Get Started"}
      </h1>

      {!user ? (
        <div className="section text-center">
          <button onClick={signInWithGoogle} className="footer-button">
            Login with Google
          </button>
        </div>
      ) : (
        <>
          {/* Toggle row */}
          <ProjectSectionHeader
            collapsed={projectsCollapsed}
            onToggle={() => setProjectsCollapsed((v) => !v)}
          />

          {/* Projects Section (toggleable) */}
          {!projectsCollapsed && (
            <div id="projects-section" className="section transition-all">
              {projectsLoading ? (
                <div className="text-center">Loading your projects…</div>
              ) : projectsError ? (
                <div className="text-center text-red-600">{projectsError}</div>
              ) : projects && projects.length > 0 ? (
                <>
                  <ul className="project-list">
                    {projects.map((p) => (
                      <li
                        key={p.id}
                        className={`project-card ${selectedProject && selectedProject.id === p.id ? "active" : ""}`}
                      >
                        <div className="project-card-main">
                          <div className="project-name">
                            {p.projectName || p.address || "Untitled Project"}
                          </div>
                          <div className="project-meta">
                            {p.created_at
                              ? new Date(p.created_at).toLocaleString()
                              : ""}
                          </div>
                        </div>
                        <div className="project-card-actions">
                          <button
                            className="footer-button"
                            onClick={() => handleDesign(p)}
                            aria-label="Design with Harmony"
                            title="Open this project in Design Harmony"
                          >
                            Design with Harmony →
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {!selectedProject && (
                    <div className="text-center mt-8">
                      <button
                        onClick={handleCreateProject}
                        className="footer-button secondary"
                      >
                        Create Another Project
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="section text-center">
                  <h3 className="mb-4">Start Your First Project</h3>
                  <button
                    onClick={handleCreateProject}
                    className="footer-button"
                  >
                    Create a New Project →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Chat Panel (always visible when a project is chosen) */}
          {selectedProject && (
            <div className="chat-panel mt-8" aria-label="ChatPanel">
              <ChatPanel project={selectedProject} room={roomDetails} />
            </div>
          )}

          {/* Logout */}
          <div className="section text-center">
            <div className="footer-actions">
              <button
                onClick={handleLogout}
                className="footer-button logout-btn"
              >
                Logout
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
